// client/src/pwa/registerServiceWorker.js — one service worker for the whole site.
//
// Imported once from index.js. It:
//   - registers /sw.js with scope "/" (production builds, https or localhost only);
//   - unregisters the client portal's retired worker (/client-portal/sw.js);
//   - keeps the browser's install prompt (beforeinstallprompt fires once, early, often
//     before the header that shows the Install button has mounted);
//   - notices a waiting new version and lets <PwaUpdateBanner> offer "Reload";
//   - asks the server for a new version when the app comes back to the foreground and
//     once an hour, so an app left open for days still updates.
// State is a tiny store read with usePwaState() (useSyncExternalStore).
import { useSyncExternalStore } from 'react';
import { SW_URL, SW_SCOPE, isLegacyRegistration, watchForUpdate, applyUpdate, UPDATE_CHECK_MS } from './pwaCore';

let state = { waitingWorker: null, installPrompt: null, installed: false };
const listeners = new Set();
const set = (patch) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const getSnapshot = () => state;

export const usePwaState = () => useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

const secureContext = () => typeof window !== 'undefined'
    && (window.location.protocol === 'https:' || ['localhost', '127.0.0.1'].includes(window.location.hostname));

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); set({ installPrompt: e }); });
    window.addEventListener('appinstalled', () => set({ installPrompt: null, installed: true }));
}

/** Show the browser's install dialog (Android / desktop Chrome and Edge). */
export async function promptInstall() {
    const e = state.installPrompt;
    if (!e) return 'unavailable';
    e.prompt();
    let outcome = 'dismissed';
    try { outcome = (await e.userChoice).outcome; } catch { /* ignore */ }
    set({ installPrompt: null, installed: outcome === 'accepted' || state.installed });
    return outcome;
}

/** "Reload" on the update banner. */
export function reloadToUpdate() {
    const sw = 'serviceWorker' in navigator ? navigator.serviceWorker : null;
    applyUpdate(state.waitingWorker, sw, () => window.location.reload());
}

export function registerServiceWorker() {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !secureContext()) return;
    // `npm start` (development) keeps the browser's normal behaviour; the worker is for builds.
    if (process.env.NODE_ENV !== 'production') return;
    const sw = navigator.serviceWorker;
    window.addEventListener('load', () => {
        sw.getRegistrations()
            .then((regs) => Promise.all(regs.filter((r) => isLegacyRegistration(r.scope, window.location.origin)).map((r) => r.unregister())))
            .catch(() => null)
            .then(() => sw.register(SW_URL, { scope: SW_SCOPE }))
            .then((reg) => {
                watchForUpdate(reg, sw, (worker) => set({ waitingWorker: worker }));
                const check = () => { reg.update().catch(() => null); };
                document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
                window.setInterval(check, UPDATE_CHECK_MS);
            })
            .catch(() => { /* the portal works without it */ });
    });
}
